import { useEffect } from 'react'
import { AnimatePresence, MotionConfig, motion } from 'motion/react'
import { useAnnotations } from '@/contexts/AnnotationsContext'

const TOAST_MS = 6000

/** Bottom-center toast shown after each persisted annotation action, offering a
 *  single-level Undo (also bound to Cmd/Ctrl+Z while visible). Reads
 *  `lastAction` from AnnotationsContext; springs up when one lands, fades down when it clears. */
export function UndoToast() {
  const annotations = useAnnotations()
  const lastAction = annotations?.lastAction ?? null

  // Auto-dismiss after TOAST_MS
  useEffect(() => {
    if (!lastAction || !annotations) return
    const timer = window.setTimeout(() => annotations.dismissLastAction(), TOAST_MS)
    return () => window.clearTimeout(timer)
  }, [lastAction, annotations])

  // Cmd/Ctrl+Z triggers undo while the action is undoable
  useEffect(() => {
    if (!lastAction || !annotations) return
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        void annotations.undoLastAction()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [lastAction, annotations])

  return (
    // Above the zoom controls, solid near-black in BOTH themes — the old glass
    // panel was white-on-pale-tissue in light mode and easy to miss entirely.
    <div className="pointer-events-none absolute bottom-32 left-1/2 z-50 -translate-x-1/2">
      {/* Tuned in /labs/undo-toast (variant B). reducedMotion="user" keeps only the fade. */}
      <MotionConfig reducedMotion="user">
        <AnimatePresence>
          {lastAction && annotations && (
            <motion.div
              key="toast"
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{
                opacity: 0,
                y: 8,
                scale: 0.98,
                transition: { duration: 0.18, ease: 'easeOut' },
              }}
              transition={{ type: 'spring', duration: 0.3, bounce: 0.1 }}
              style={{ transformOrigin: '50% 100%' }}
              className="pointer-events-auto flex items-center gap-3 rounded-modal border border-white/20 bg-[#1c1c1e]/95 py-2 pl-4 pr-2 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
            >
              <span className="whitespace-nowrap text-body-small text-white">
                {lastAction.label}
              </span>
              <button
                type="button"
                onClick={() => void annotations.undoLastAction()}
                className="rounded-md bg-white/10 px-3 py-1 text-body-small font-semibold text-[#69a9ff] transition-colors hover:bg-white/20"
              >
                Undo
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </MotionConfig>
    </div>
  )
}
